// Design system domain types
// All types related to design system tokens, categories, and content structure

// Design system content category (for Redux state organization)
export type DesignSystemCategory = 'tokens' | 'styles' | 'components';

// Formal mapping from CSS comment text to TypeScript category type values
export const CATEGORY_COMMENT_MAP = {
	Colors: 'color',
	Typography: 'typography',
	Spacing: 'spacing',
	Elevation: 'elevation',
	'Border Radius': 'borderRadius',
	Opacity: 'opacity',
	Motion: 'motion',
	Other: 'other',
} as const;

export type TokenCategory = (typeof CATEGORY_COMMENT_MAP)[keyof typeof CATEGORY_COMMENT_MAP];

// Formal mapping from CSS comment text to Typography subcategory type values
export const TYPOGRAPHY_SUBCATEGORY_COMMENT_MAP = {
	'Font Families': 'fontFamilies',
	'Font Sizes': 'fontSizes',
	'Line Heights': 'lineHeights',
	'Font Weights': 'fontWeights',
} as const;

export type TypographySubcategory =
	(typeof TYPOGRAPHY_SUBCATEGORY_COMMENT_MAP)[keyof typeof TYPOGRAPHY_SUBCATEGORY_COMMENT_MAP];

// Formal mapping from CSS comment text to Color subcategory type values
export const COLOR_SUBCATEGORY_COMMENT_MAP = {
	'Page + surfaces': 'pageSurfaces',
	Text: 'text',
	'Accent / brand': 'accentBrand',
	Semantic: 'semantic',
	Overlay: 'overlay',
} as const;

export type ColorSubcategory =
	(typeof COLOR_SUBCATEGORY_COMMENT_MAP)[keyof typeof COLOR_SUBCATEGORY_COMMENT_MAP];

// Union of all subcategories
export type TokenSubcategory = TypographySubcategory | ColorSubcategory | string;

// Base token value interface
export interface BaseTokenValue {
	kind: string;
	original: string; // Raw CSS value, e.g., "#f8fbff" or "0.5rem"
}

// Specific token value types extending BaseTokenValue
export interface ColorTokenValue extends BaseTokenValue {
	kind: 'color';
	hex: string;
	rgb: { r: number; g: number; b: number; a?: number };
	hsl?: { h: number; s: number; l: number; a?: number };
}

export interface LengthTokenValue extends BaseTokenValue {
	kind: 'length';
	rem?: string;
	px?: number;
	em?: string;
	unit: 'rem' | 'px' | 'em' | 'pt' | 'pc' | 'in' | 'cm' | 'mm';
	numericValue: number;
}

export interface ShadowTokenValue extends BaseTokenValue {
	kind: 'shadow';
	offsetX: number;
	offsetY: number;
	blur: number;
	spread?: number;
	color: ColorTokenValue;
}

export interface FontFamilyTokenValue extends BaseTokenValue {
	kind: 'fontFamily';
	families: string[]; // e.g., ["system-ui", "Inter", "sans-serif"]
}

export interface FontSizeTokenValue extends BaseTokenValue {
	kind: 'fontSize';
	length: LengthTokenValue; // Font sizes are lengths
}

export interface LineHeightTokenValue extends BaseTokenValue {
	kind: 'lineHeight';
	numeric?: number; // e.g., 1.5
	length?: LengthTokenValue; // e.g., "24px"
}

export interface FontWeightTokenValue extends BaseTokenValue {
	kind: 'fontWeight';
	numeric: number; // e.g., 400, 500, 600, 700
	keyword?: 'normal' | 'bold' | 'lighter' | 'bolder';
}

export interface BorderRadiusTokenValue extends BaseTokenValue {
	kind: 'borderRadius';
	length: LengthTokenValue; // Border radius values are lengths
}

export interface OpacityTokenValue extends BaseTokenValue {
	kind: 'opacity';
	value: number; // 0-1
}

export interface TransitionTokenValue extends BaseTokenValue {
	kind: 'transition';
	duration: string; // e.g., "0.16s"
	timingFunction: string; // e.g., "ease-out"
	property?: string;
	delay?: string;
}

export interface NumberTokenValue extends BaseTokenValue {
	kind: 'number';
	value: number;
}

export interface StringTokenValue extends BaseTokenValue {
	kind: 'string';
}

// Discriminated union of all token value types
export type TokenValue =
	| ColorTokenValue
	| LengthTokenValue
	| ShadowTokenValue
	| FontFamilyTokenValue
	| FontSizeTokenValue
	| LineHeightTokenValue
	| FontWeightTokenValue
	| BorderRadiusTokenValue
	| OpacityTokenValue
	| TransitionTokenValue
	| NumberTokenValue
	| StringTokenValue;

// Main token interface - stores value directly (no separate valueType or metadata)
export interface DesignSystemToken {
	id: string; // e.g., "color-bg"
	name: string; // e.g., "Color Background" (generated from CSS variable name)
	cssVariable: string; // e.g., "--ds-color-bg"
	category: TokenCategory; // From CSS comment (may be wrong - use value.kind for display)
	subcategory?: TokenSubcategory; // From CSS comment
	value: TokenValue; // Structured value with kind field (determines how to display)
}

// Primitive category mapping from CSS comment sections
export const PRIMITIVE_CATEGORY_COMMENT_MAP = {
	'Layout primitives': 'layout',
	'Typography primitives': 'typography',
	'Card primitive': 'card',
	'Button primitive': 'button',
	'Input / field primitives': 'form',
	'Search primitives': 'form',
	'Sidebar primitives': 'navigation',
	'Topbar primitives': 'navigation',
	'Footer primitives': 'navigation',
	'Breadcrumb primitives': 'navigation',
	'Tabs primitives': 'navigation',
	'Hero section primitives': 'layout',
	'Action section primitives': 'layout',
	'Container utilities': 'layout',
	'Spacer utilities': 'layout',
} as const;

export type PrimitiveCategory =
	| 'layout'
	| 'typography'
	| 'card'
	| 'button'
	| 'form'
	| 'navigation'
	| 'other';

// CSS property interface
export interface CSSProperty {
	name: string; // e.g., "background", "border-radius"
	value: string; // e.g., "linear-gradient(...)", "0.5rem"
}

// Media query interface
export interface MediaQuery {
	condition: string; // Raw condition: "(max-width: 900px)"
}

// CSS rule inside a media query
export interface CSSMediaQuery {
	fullSelector: string; // ".app-shell" (base selector, may include pseudo-class)
	properties: CSSProperty[];
	mediaQuery: MediaQuery;
}

// CSS rule with pseudo-class
export interface CSSPseudo {
	fullSelector: string; // ".app-shell:hover"
	properties: CSSProperty[];
	pseudoClass: string; // "hover", "focus", "active", etc.
}

// CSS rule with both media query AND pseudo-class
export interface CSSMediaQueryPseudo {
	fullSelector: string; // ".app-shell:hover"
	properties: CSSProperty[];
	mediaQuery: MediaQuery;
	pseudoClass: string;
}

// Main primitive interface - stores CSS class information
export interface DesignSystemPrimitive {
	id: string; // e.g., "app-shell" (base selector only)
	name: string; // e.g., "App Shell" (generated from CSS class name)
	baseSelector: string; // e.g., ".app-shell" (the base class)
	category: PrimitiveCategory; // From CSS comment section
	subcategory?: string; // Optional subcategory (e.g., "Buttons")
	description?: string; // Optional comment/description from CSS

	// Base properties (from the main rule, no media query, no pseudo-class)
	properties: CSSProperty[];

	// Variations: media queries and pseudo-classes
	mediaQueries?: CSSMediaQuery[]; // Rules inside @media blocks
	pseudoClasses?: CSSPseudo[]; // Rules with :hover, :focus, etc.
	mediaQueryPseudos?: CSSMediaQueryPseudo[]; // Rules with both (less common)

	// Nested rules: descendant selectors scoped to this primitive
	// Can be any variation type
	nestedRules?: Array<CSSMediaQuery | CSSPseudo | CSSMediaQueryPseudo>;

	// Full CSS source - includes ALL rules (base + variations + nested) as editable text
	fullCSS: string;
}

// Component prop interface
export interface ComponentProp {
	name: string; // e.g., "label", "variant"
	type: 'string' | 'number' | 'boolean' | 'json' | 'select';
	defaultValue?: string; // Default value from component code
	description?: string; // From comments or inferred
	options?: string[]; // For select/boolean types
	required?: boolean; // Whether prop is required
}

// Component category type
export type ComponentCategory =
	| 'button'
	| 'card'
	| 'form'
	| 'navigation'
	| 'data-display'
	| 'other';

// Main component interface - stores web component information
export interface DesignSystemComponent {
	id: string; // e.g., "ds-button"
	name: string; // e.g., "Button"
	tagName: string; // e.g., "ds-button"
	category: ComponentCategory; // e.g., "button", "card", "form"
	description?: string; // From comment before component
	props: ComponentProp[]; // Parsed component attributes/props
	exampleHTML?: string; // Example usage HTML
}

// Design system story interface - represents a story/example for the design system
export interface DesignSystemStory {
	title: string;
	code: string;
	isValidHtml?: boolean; // Flag indicating if the code contains valid HTML tags
}
